'use client';

import {
  Box,
  Button,
  Flex,
  FormHelperText,
  FormLabel,
  freddyTheme,
  Typography
} from '@coolblue-development/freddy';
import selectRemoveUploadTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/selectRemoveUploadTrigger';
import selectUploadTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/selectUploadTrigger';
import showUploadMessageTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/showUploadMessageTrigger';
import { Paperclip, Upload } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { DocumentType, Rules } from '@sharedTypes';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

type Props = {
  name: string;
  documentType: DocumentType;
  label: string;
  buttonText: string;
  rules?: Rules;
};

export const FileUploadHelper = ({ name, documentType, label, buttonText, rules }: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    register,
    setError,
    clearErrors,
    setValue,
    formState: { errors }
  } = useFormContext();

  const { trackEvent } = useAnalytics();

  const subFeature = documentType === DocumentType.Resume ? 'cv' : 'motivation_letter';
  const trackUploadMessage = (content: string) => {
    trackEvent(
      showUploadMessageTrigger({
        sub_feature: subFeature,
        content
      })
    );
  };

  const errorMessage = errors[name]?.message;
  if (errorMessage) trackUploadMessage(errorMessage.toString());

  const { t } = useTranslation('vacancyDetail.applicationForm.questions.uploadFile');

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const validateAndSetSelectedFile = (file: File) => {
    const maxFileSize = rules?.maxFileSize?.value;
    if (maxFileSize && file.size > maxFileSize) {
      setError(name, {
        type: 'manual',
        message: rules?.maxFileSize?.message
      });

      return;
    }

    clearErrors(name);
    trackUploadMessage('success');

    toBase64(file).then(value => {
      setValue(name, value);
    });
    setSelectedFile(file);
  };

  const issue = true; // Temporarily disable file uploads because of technical issues

  return (
    <Flex direction='column' alignItems='baseline' gap={200}>
      <FormLabel sx={{ color: freddyTheme.color.font.interactiveDisabled }}>{label}</FormLabel>

      {!selectedFile && (
        <Button
          // Temporarily disable file uploads because of technical issues, the component has to be set back to div after the issue is resolved because of accessibility reasons
          disabled
          // component='div'
          variant='secondary'
          fullWidthUntil='md'
          startIcon={<Upload />}
          onClick={() => {
            document.getElementById(name)?.click();
            selectUploadTrigger({ sub_feature: subFeature });
          }}
        >
          {buttonText}
          {/* Temporarily disable file uploads because of technical issues without removing all code and ts errors */}
          {issue ? undefined : (
            <input
              type='file'
              id={name}
              accept={rules?.fileType?.value ?? 'application/pdf'}
              {...register(name, {
                required: rules?.required
              })}
              onChange={e => {
                if (e.target.files) validateAndSetSelectedFile(e.target.files[0]);
              }}
              hidden
            />
          )}
        </Button>
      )}

      {selectedFile && (
        <Flex
          justifyContent='space-between'
          alignItems='center'
          gap={200}
          border='1px solid'
          borderColor='base'
          borderRadius={100}
          padding={200}
          sx={{ width: '100%' }}
        >
          <Paperclip color={freddyTheme.color.icon.subtle} />
          <Typography flexGrow={2} color='subtle' marginBottom={0} truncateLines={1}>
            {selectedFile.name}
          </Typography>
          <Box
            component='button'
            color='interactiveBase'
            onClick={() => {
              setSelectedFile(null);
              trackEvent(selectRemoveUploadTrigger({ sub_feature: subFeature }));
            }}
            sx={{ all: 'unset', '&:hover': { cursor: 'pointer' } }}
          >
            {t('deleteButton')}
          </Box>
        </Flex>
      )}

      {errorMessage && (
        <FormHelperText validation='error'>{errorMessage.toString()}</FormHelperText>
      )}
    </Flex>
  );
};
